"use strict";

/* -------------------------------------------------------------------------
 * Login / registrazione area professionisti
 * ---------------------------------------------------------------------- */

const LOGIN_FORM_SELECTOR = "[data-professional-login]";
const LOGIN_NOTIFY_SELECTOR = "[data-professional-login-notify]";
const LOGIN_SUBMIT_SELECTOR = "[data-professional-login-submit]";
const LOGIN_ACTION = "professional_login_action";

const SIGNUP_FORM_SELECTOR = "[data-professional-signup]";
const SIGNUP_NOTIFY_SELECTOR = "[data-professional-signup-notify]";
const SIGNUP_SUBMIT_SELECTOR = "[data-professional-signup-submit]";
const SIGNUP_ACTION = "professional_signup_action";

const PASSWORD_TOGGLE_SELECTOR = "[data-password-toggle]";

/* -------------------------------------------------------------------------
 * Archivio area professionisti (tab categorie + preferiti)
 * ---------------------------------------------------------------------- */

const ARCHIVE_SELECTOR = "[data-resource-archive]";
const TABS_NAV_SELECTOR = ".resources-tabs-nav";
const TABS_SELECTOR = "[data-resource-tabs]";
const TAB_BUTTON_SELECTOR = "[data-resource-tab]";
const RESULTS_SELECTOR = "[data-resource-results]";
const SCROLL_EDGE_TOLERANCE = 2;

const TAB_ACTION = "resource_tab_action";
const FAVORITES_ACTION = "favorites_list_action";

function setNotify(notify, type, message) {
    if (!notify) {
        return;
    }

    notify.className = "form-notify";

    if (type) {
        notify.classList.add(type);
    }

    notify.textContent = message || "";
}

function initPasswordToggles(root = document) {
    root.querySelectorAll(PASSWORD_TOGGLE_SELECTOR).forEach((button) => {
        if (button.dataset.passwordToggleReady === "1") {
            return;
        }

        button.dataset.passwordToggleReady = "1";

        const field = button.closest(".form-password");
        const input = field?.querySelector('input[type="password"], input[type="text"]');
        const iconShow = button.querySelector(".form-password-toggle-icon--show");
        const iconHide = button.querySelector(".form-password-toggle-icon--hide");

        if (!input) {
            return;
        }

        button.addEventListener("click", () => {
            const willShow = input.type === "password";

            input.type = willShow ? "text" : "password";
            button.setAttribute("aria-pressed", String(willShow));
            button.setAttribute(
                "aria-label",
                willShow
                    ? (button.dataset.labelHide || "Nascondi password")
                    : (button.dataset.labelShow || "Mostra password")
            );

            // Occhio barrato = password nascosta; occhio aperto = password visibile.
            if (iconShow) {
                iconShow.hidden = !willShow;
            }

            if (iconHide) {
                iconHide.hidden = willShow;
            }
        });
    });
}

async function submitProfessionalForm({ form, notify, submitButton, action }) {
    if (!window.WPURLS?.ajaxUrl) {
        return;
    }

    if (!form.checkValidity()) {
        form.reportValidity();
        return;
    }

    const defaultMessage = window.WPURLS.mail_message?.error || "";

    if (submitButton) {
        submitButton.disabled = true;
    }

    setNotify(notify, "info", window.WPURLS.mail_message?.sending || "");

    const data = new FormData(form);
    data.append("action", action);

    try {
        const response = await fetch(window.WPURLS.ajaxUrl, {
            method: "POST",
            credentials: "same-origin",
            body: data,
        });

        const payload = await response.json();
        const isSuccess = Boolean(payload?.success);
        const message = payload?.data?.message || defaultMessage;

        if (isSuccess && payload?.data?.redirect) {
            window.location.assign(payload.data.redirect);
            return;
        }

        setNotify(notify, isSuccess ? "success" : "error", message);
    } catch (error) {
        setNotify(notify, "error", defaultMessage);
    } finally {
        if (submitButton) {
            submitButton.disabled = false;
        }
    }
}

function initProfessionalLogin() {
    const form = document.querySelector(LOGIN_FORM_SELECTOR);

    if (!form) {
        return;
    }

    const notify = form.querySelector(LOGIN_NOTIFY_SELECTOR);
    const submitButton = form.querySelector(LOGIN_SUBMIT_SELECTOR);

    form.addEventListener("submit", (event) => {
        event.preventDefault();
        submitProfessionalForm({
            form,
            notify,
            submitButton,
            action: LOGIN_ACTION,
        });
    });
}

function initProfessionalSignup() {
    const form = document.querySelector(SIGNUP_FORM_SELECTOR);

    if (!form) {
        return;
    }

    const notify = form.querySelector(SIGNUP_NOTIFY_SELECTOR);
    const submitButton = form.querySelector(SIGNUP_SUBMIT_SELECTOR);

    form.addEventListener("submit", (event) => {
        event.preventDefault();
        submitProfessionalForm({
            form,
            notify,
            submitButton,
            action: SIGNUP_ACTION,
        });
    });
}

async function fetchTab(tabValue) {
    const isFavorites = tabValue === "favorites";
    const data = new FormData();

    data.append("action", isFavorites ? FAVORITES_ACTION : TAB_ACTION);
    data.append("nonce", window.WPURLS.nonce);

    if (!isFavorites) {
        data.append("category", tabValue);
    }

    const response = await fetch(window.WPURLS.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: data,
    });

    const payload = await response.json();

    return payload && payload.success ? payload.data : null;
}

function setActiveTab(tabs, activeValue) {
    tabs.querySelectorAll(TAB_BUTTON_SELECTOR).forEach((button) => {
        const isActive = button.getAttribute("data-resource-tab") === String(activeValue);
        button.setAttribute("aria-selected", String(isActive));
        button.tabIndex = isActive ? 0 : -1;
    });
}

function updateScrollHints(nav, list) {
    const maxScrollLeft = list.scrollWidth - list.clientWidth;
    const isScrollable = maxScrollLeft > SCROLL_EDGE_TOLERANCE;

    nav.classList.toggle("is-scrollable", isScrollable);
    nav.classList.toggle("can-scroll-left", isScrollable && list.scrollLeft > SCROLL_EDGE_TOLERANCE);
    nav.classList.toggle(
        "can-scroll-right",
        isScrollable && list.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE
    );
}

function scrollTabIntoView(list, tab, { smooth = true } = {}) {
    if (!list || !tab) {
        return;
    }

    const listRect = list.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const isFullyVisible =
        tabRect.left >= listRect.left + SCROLL_EDGE_TOLERANCE &&
        tabRect.right <= listRect.right - SCROLL_EDGE_TOLERANCE;

    if (isFullyVisible) {
        return;
    }

    const target = tab.offsetLeft - list.clientWidth / 2 + tab.offsetWidth / 2;

    list.scrollTo({
        left: Math.max(0, target),
        behavior: smooth ? "smooth" : "auto",
    });
}

function initProfessionalArchive() {
    const archive = document.querySelector(ARCHIVE_SELECTOR);

    if (!archive || !window.WPURLS) {
        return;
    }

    const nav = archive.querySelector(TABS_NAV_SELECTOR);
    const tabs = archive.querySelector(TABS_SELECTOR);
    const results = archive.querySelector(RESULTS_SELECTOR);

    if (!tabs || !results) {
        return;
    }

    let isLoading = false;

    function syncScrollHints() {
        if (nav) {
            updateScrollHints(nav, tabs);
        }
    }

    async function loadTab(tabValue, { scrollIntoView = true } = {}) {
        if (isLoading) {
            return;
        }

        isLoading = true;
        results.setAttribute("aria-busy", "true");

        try {
            const payload = await fetchTab(tabValue);

            if (payload && typeof payload.html === "string") {
                results.innerHTML = payload.html;
                archive.dataset.resourceActive = String(tabValue);
                setActiveTab(tabs, tabValue);

                if (scrollIntoView) {
                    const activeTab = tabs.querySelector(
                        `${TAB_BUTTON_SELECTOR}[data-resource-tab="${CSS.escape(String(tabValue))}"]`
                    );
                    scrollTabIntoView(tabs, activeTab);
                }
            }
        } finally {
            isLoading = false;
            results.setAttribute("aria-busy", "false");
            syncScrollHints();
        }
    }

    tabs.addEventListener("click", (event) => {
        const button = event.target.closest(TAB_BUTTON_SELECTOR);

        if (!button) {
            return;
        }

        const tabValue = button.getAttribute("data-resource-tab");

        if (!tabValue || tabValue === archive.dataset.resourceActive) {
            return;
        }

        loadTab(tabValue);
    });

    tabs.addEventListener("keydown", (event) => {
        const current = event.target.closest(TAB_BUTTON_SELECTOR);

        if (!current) {
            return;
        }

        const buttons = [...tabs.querySelectorAll(TAB_BUTTON_SELECTOR)];
        const index = buttons.indexOf(current);
        let nextIndex = -1;

        if (event.key === "ArrowRight") {
            nextIndex = (index + 1) % buttons.length;
        } else if (event.key === "ArrowLeft") {
            nextIndex = (index - 1 + buttons.length) % buttons.length;
        } else if (event.key === "Home") {
            nextIndex = 0;
        } else if (event.key === "End") {
            nextIndex = buttons.length - 1;
        }

        if (nextIndex < 0) {
            return;
        }

        event.preventDefault();
        buttons[nextIndex].focus();

        const tabValue = buttons[nextIndex].getAttribute("data-resource-tab");

        if (tabValue && tabValue !== archive.dataset.resourceActive) {
            loadTab(tabValue);
        }
    });

    if (nav) {
        tabs.addEventListener("scroll", syncScrollHints, { passive: true });
        window.addEventListener("resize", syncScrollHints, { passive: true });

        tabs.querySelectorAll(TAB_BUTTON_SELECTOR).forEach((button) => {
            button.addEventListener("focus", () => {
                scrollTabIntoView(tabs, button, { smooth: false });
            });
        });

        if (typeof ResizeObserver !== "undefined") {
            const resizeObserver = new ResizeObserver(syncScrollHints);
            resizeObserver.observe(tabs);
        }

        syncScrollHints();
    }
}

function init() {
    initPasswordToggles();
    initProfessionalLogin();
    initProfessionalSignup();
    initProfessionalArchive();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
    init();
}
